import { html, LitElement, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import { NAMESPACE } from "@/constants";
import { genRandSeed } from "@/utils";

import {
  ALERT_ICON_FRAGMENTS,
  DEFAULT_ALERT_PROPS as DEFAULT_PROPS,
} from "./alert.defs";
import type {
  AlertPalette,
  AlertPart,
  AlertStatus,
  AlertVariant,
  SbAlertProps as Props,
} from "./alert.types";

/**
 * Displays a brief, important message for users in an unobtrusive manner.
 *
 * @part alert - rtg-alert: root
 * @part alert-icon - svg
 * @part alert-title - rtg-alert-title
 * @part alert-description - rtg-alert-description
 */
@customElement("sb-alert")
export class SbAlert extends LitElement implements Props {
  static readonly ROOT = "alert";
  static readonly ICON = "icon";
  static readonly TITLE = "title";
  static readonly DESCRIPTION = "description";

  static readonly TAG = `${NAMESPACE}-${SbAlert.ROOT}`;

  static readonly PARTS: Record<AlertPart, string> = {
    root: SbAlert.ROOT,
    icon: `${SbAlert.ROOT}-${SbAlert.ICON}`,
    title: `${SbAlert.ROOT}-${SbAlert.TITLE}`,
    description: `${SbAlert.ROOT}-${SbAlert.DESCRIPTION}`,
  };

  /* ------------------------------------------------------------ */

  /**
   * Substring used in generated part IDs.
   */
  @property({ type: String })
  seed = genRandSeed();

  /**
   * Custom ID used as the root part ID and as the base of subpart IDs.
   */
  @property({ type: String, attribute: "custom-id" })
  customId?: string;

  /**
   * Status communicated by the alert component.
   */
  @property({ type: String })
  status: AlertStatus = DEFAULT_PROPS.status;

  /**
   * Color palette used by the alert component.
   */
  @property({ type: String })
  palette: AlertPalette = DEFAULT_PROPS.palette;

  /**
   * Visual variant of the alert component.
   */
  @property({ type: String })
  variant: AlertVariant = DEFAULT_PROPS.variant;

  /**
   * Text used in the title part of alert.
   */
  private _title?: string;

  private get _defaultTitle() {
    if (this.status === "default") return "Alert";
    return this.status.charAt(0).toUpperCase() + this.status.slice(1);
  }

  @property({ type: String })
  get title(): string {
    return this._title ?? this._defaultTitle;
  }

  set title(value: string | null | undefined) {
    const old = this.title;
    this._title = value || undefined;
    this.requestUpdate("title", old);
  }

  /**
   * Text used in the description part of alert.
   */
  @property({ type: String })
  description?: string;

  /**
   * Whether the icon part of alert is rendered.
   */
  @property({ type: Boolean })
  icon?: boolean;

  /* ------------------------------------------------------------ */

  protected createRenderRoot() {
    return this;
  }

  /* ------------------------------------------------------------ */

  get rootId() {
    return this.customId ?? `${SbAlert.TAG}-${this.seed}`;
  }

  get iconId() {
    return `${this.rootId}-${SbAlert.ICON}`;
  }

  get titleId() {
    return `${this.rootId}-${SbAlert.TITLE}`;
  }

  get descriptionId() {
    return `${this.rootId}-${SbAlert.DESCRIPTION}`;
  }

  /* ---------------------------------------- */

  private get _rootVariant() {
    let variant =
      this.palette === "base"
        ? "default"
        : this.palette === "brand"
          ? "brand"
          : this.status === "error"
            ? "destructive"
            : this.status;

    if (this.variant === "tonal" && variant !== "default") variant += "-tonal";

    return variant;
  }

  /* ------------------------------------------------------------ */

  private _renderIcon() {
    if (!this.icon) return nothing;
    const fragments = ALERT_ICON_FRAGMENTS[this.status];

    return html`
      <svg
        id="${this.iconId}"
        part="${SbAlert.PARTS.icon}"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        ${fragments}
      </svg>
    `;
  }

  /* ---------------------------------------- */

  render() {
    return html`
      <rtg-alert
        id="${this.rootId}"
        part="${SbAlert.PARTS.root}"
        data-status="${this.status}"
        data-palette="${this.palette}"
        data-variant="${this.variant}"
        variant="${this._rootVariant}"
        role="status"
      >
        ${this._renderIcon()}

        <rtg-alert-title id="${this.titleId}" part="${SbAlert.PARTS.title}">
          ${this.title}
        </rtg-alert-title>

        ${this.description
          ? html`
              <rtg-alert-description
                id="${this.descriptionId}"
                part="${SbAlert.PARTS.description}"
              >
                ${this.description}
              </rtg-alert-description>
            `
          : nothing}
      </rtg-alert>
    `;
  }
}
